import { Link } from "react-router-dom";
import { mediaUrl } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useCartUI } from "@/lib/cartui";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { useStoreBase } from "@/lib/store";
import { useTranslation } from "react-i18next";
import { money, symbolOf } from "@/lib/money";
import { storeOrdersPaused, storeOfflineMessage } from "@/lib/timeslotCheckout";

export default function StorefrontCartDrawer() {
  const { slug, base, storeConfig } = useStoreBase();
  const paused = storeOrdersPaused(storeConfig);
  const { t } = useTranslation();
  const { items, setQty, remove, subtotal, count } = useCart();
  const { cartOpen, closeCart } = useCartUI();

  return (
    <Sheet open={cartOpen} onOpenChange={(o) => (o ? null : closeCart())}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="cart-drawer">
        <SheetHeader className="px-5 py-4 border-b border-stone-200">
          <SheetTitle className="flex items-center gap-2 font-display text-lg">
            <ShoppingBag className="w-5 h-5" /> {t("store.yourCart")} {count > 0 && <span className="text-stone-400 font-normal">({count})</span>}
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <ShoppingBag className="w-12 h-12 text-stone-300" />
            <div className="mt-4 font-display font-semibold text-stone-900">{t("store.emptyCart")}</div>
            <p className="text-stone-500 text-sm mt-1">{t("store.emptyCartHint")}</p>
            <Link to={`${base}/shop`} onClick={closeCart}><Button className="mt-6 btn-store-primary rounded-full">{t("store.startShopping")}</Button></Link>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {items.map((i) => {
                const k = i.line_id || `${i.product_id}${i.variant_id ? `::${i.variant_id}` : ""}`;
                const freeze = Boolean(i.freeze_qty);
                return (
                  <div key={k} className="flex gap-3" data-testid={`cart-drawer-item`}>
                    <div className="w-16 h-16 rounded-xl overflow-hidden bg-stone-100 flex-shrink-0">
                      {i.image ? <img src={mediaUrl(i.image)} alt={i.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-stone-300 font-display">{i.name.slice(0, 1)}</div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-stone-900 line-clamp-2">{i.name}</div>
                      <div className="font-mono-nums text-sm font-bold text-stone-900 mt-0.5">{money(i.price * i.qty, symbolOf(i))}</div>
                      <div className="mt-1.5 flex items-center gap-2">
                        {freeze ? (
                          <span className="text-xs text-stone-500">{i.qty} pcs</span>
                        ) : (
                        <div className="inline-flex items-center rounded-full border border-stone-200">
                          <button className="w-7 h-7 flex items-center justify-center text-stone-600 hover:text-stone-900" onClick={() => setQty(i.product_id, i.variant_id, i.qty - 1, i.line_id)} aria-label="Decrease"><Minus className="w-3.5 h-3.5" /></button>
                          <span className="w-7 text-center text-sm font-mono-nums">{i.qty}</span>
                          <button className="w-7 h-7 flex items-center justify-center text-stone-600 hover:text-stone-900" onClick={() => setQty(i.product_id, i.variant_id, i.qty + 1, i.line_id)} aria-label="Increase"><Plus className="w-3.5 h-3.5" /></button>
                        </div>
                        )}
                        <button className="text-stone-400 hover:text-red-600" onClick={() => remove(i.product_id, i.variant_id, i.line_id)} aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-stone-200 p-5 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-stone-500">Subtotal</span>
                <span className="font-mono-nums font-bold text-stone-900 text-base">{money(subtotal, symbolOf(items[0]))}</span>
              </div>
              {paused ? (
                <div className="text-xs text-stone-600 mb-2" data-testid="cart-drawer-offline">{storeOfflineMessage(storeConfig)}</div>
              ) : null}
              <Link to={paused ? "#" : `${base}/checkout`} onClick={(e) => { if (paused) e.preventDefault(); else closeCart(); }} data-testid="cart-drawer-checkout">
                <Button disabled={paused} className="w-full btn-store-primary rounded-full h-12 text-base font-semibold">{t("store.checkout")} <ArrowRight className="ml-2 w-4 h-4" /></Button>
              </Link>
              <Link to={`${base}/cart`} onClick={closeCart} className="block text-center text-sm text-stone-500 hover:text-stone-900">View full cart</Link>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
